import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { InformationIcon, PrintIcon, UserCanceled2Icon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Switch from '@repo/ui/switch'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { adminUpdateStaff } from '../../../api/staff/admin-update-staff'
import { deactivateStaff } from '../../../api/staff/deactivate-staff'
import { getStaffMissingDocuments } from '../../../api/staff/get-staff-missing-documents'
import { reactivateStaff } from '../../../api/staff/reactivate-staff'
import { connectAsStaff } from '../../../api/staffs/connect-as-staff'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { UserStatusEnum } from '../../../lib/types/enums/users/user-status.enum'
import {
  AdminStaffInput,
  DeactivateStaffInput
} from '../../../lib/types/models/staff/admin-staff-input'
import { Staff, StaffMissingDocument } from '../../../lib/types/models/staff/staff'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import InformationToolTip from '../../elements/information-tooltip'
import { StatusToggle } from '../../elements/status-toggle'
import { getUserDefaultRedirectionPath } from '../login/utils'
import StaffAccountStatus from './staff-account-status'
import StaffDeactivateForm from './staff-form/staff-deactivate-form'
import StaffMissingDocumentsContainer from './staff-missing-documents-container'
import { getStaffProfileMissingInformationItems } from './utils/staff-profile-missing-information'

type Props = {
  showActions?: boolean
  matricule?: string
  // the staff's user account, its status is the status of the staff space
  account?: Staff['user']
  onReactivated?: () => void
  onMissingDocumentClick: (document: StaffMissingDocument) => void
  onMissingInformationClick?: () => void
}

const StaffActionsHeader = ({
  showActions = true,
  matricule,
  account,
  onReactivated,
  onMissingDocumentClick,
  onMissingInformationClick
}: Props) => {
  const id = useParams<{ id: string }>().id
  const { downloadDocuments } = useDownloadDocuments()

  const form = useFormContext<AdminStaffInput>()
  const [deactivated, setDeactivated] = useState(
    !!form.formState.defaultValues?.deactivationReason
  )
  const [canModify, setCanModify] = useState(!!form.formState.defaultValues?.canModify)
  const additionalMissingInformation = getStaffProfileMissingInformationItems(
    form.formState.defaultValues,
    onMissingInformationClick ?? (() => {})
  )

  const { data } = useQuery({
    queryKey: ['staff-missing-documents', id],
    queryFn: () => {
      if (id && !isNaN(+id)) return getStaffMissingDocuments(+id)
    }
  })

  const { modalState, toggleModal, closeModel } = useModalManager(['deactivate'])

  const { mutate } = useMutation({
    mutationFn: reactivateStaff,
    onSuccess: () => {
      toast.success('Le membre du staff a été activé')
      form.setValue('deactivationReason', null)
      form.setValue('deactivatedAt', null)
    },
    onError: showAxiosError
  })

  const { mutate: updateStaffMutation } = useMutation({
    mutationFn: adminUpdateStaff,
    onSuccess: (data) => {
      if (data.canModify) toast.success('Les modifications sont autorisées')
      else toast.success('Les modifications ne sont plus autorisées')
    },
    onError: (error) => {
      showAxiosError(error)
      setCanModify(!canModify)
    }
  })

  const { mutate: connectAsStaffMutation, isPending: connectAsStaffPending } = useMutation({
    mutationFn: connectAsStaff,
    onError: showAxiosError,
    onSuccess: (data) => {
      // A full page load: nothing of the previous session keeps running
      window.location.assign(getUserDefaultRedirectionPath(data))
    }
  })

  const deactivateId = id ? parseInt(id) : -1

  const { mutateAsync: deactivate } = useMutation({
    mutationFn: (input: DeactivateStaffInput) => deactivateStaff(deactivateId, input),
    onSuccess: () => {
      toast.success('Le membre du staff a été désactivé')
      form.setValue('deactivatedAt', new Date())
      closeModel('deactivate')
    },
    onError: showAxiosError
  })

  return (
    <div className="w-full flex flex-col gap-5 ">
      <div className="w-full flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
            Profil encadrant
          </Typography>
          {matricule && <Badge variant="primary" text={`#${matricule}`} />}
          {showActions && <StaffAccountStatus account={account} onReactivated={onReactivated} />}
        </div>
        {showActions && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="primary"
              size="medium"
              label="Voir l'espace encadrant"
              disabled={account?.status !== UserStatusEnum.ACTIVE}
              loading={connectAsStaffPending}
              onClick={() => {
                if (!id || isNaN(+id)) return
                connectAsStaffMutation(+id)
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="medium"
              icon={PrintIcon}
              label="PDF avec commentaires"
              onClick={() => {
                if (!id || isNaN(+id)) return
                downloadDocuments({
                  ids: [parseInt(id)],
                  endpoint: API.staffs.printCompletePdf.url
                })
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="medium"
              icon={PrintIcon}
              label="PDF sans commentaires"
              onClick={() => {
                if (!id || isNaN(+id)) return
                downloadDocuments({
                  ids: [parseInt(id)],
                  endpoint: API.staffs.printPartialPdf.url
                })
              }}
            />
            <InnerBox className="flex flex-row items-center border-primary px-1 py-0">
              <Switch
                label="Autoriser les modifications dans l’espace encadrant"
                borderClassName="gap-2 rounded-lg"
                labelVariant="label-medium"
                checked={canModify}
                onCheckedChange={() => {
                  if (!id || isNaN(+id)) return
                  setCanModify(!canModify)
                  updateStaffMutation({ id: parseInt(id), canModify: !canModify })
                }}
              />
              <InformationToolTip
                contentVariant="secondary"
                size="small"
                align="start"
                icon={
                  <InformationIcon
                    width="24px"
                    height="24px"
                    color={tailwindColors.secondary.DEFAULT}
                  />
                }
              >
                <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
                  À partir de 3 semaines avant son
                  <br />
                  prochain départ, l’encadrant ne
                  <br />
                  pourra plus modifier ses informations
                  <br />
                  personnelles dans son espace
                </Typography>
              </InformationToolTip>
            </InnerBox>
            <StatusToggle
              isActive={!deactivated}
              onToggle={(checked) => {
                if (checked) {
                  setDeactivated(false)
                  if (!id || isNaN(+id)) return
                  mutate(+id)
                } else {
                  setDeactivated(true)
                  toggleModal('deactivate')
                }
              }}
            />
          </div>
        )}
        <Modal
          isOpen={modalState.deactivate}
          title={'Désactiver'}
          icon={UserCanceled2Icon}
          hideActions
          size="medium"
          onCancel={() => {
            setDeactivated(false)
            toggleModal('deactivate')
          }}
        >
          <StaffDeactivateForm
            className={MODAL_BODY_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel: () => {
                setDeactivated(false)
                closeModel('deactivate')
              },
              onConfirm: async (values) => {
                await deactivate(values)
                form.setValue('deactivationReason', values.deactivationReason)
                setDeactivated(true)
                closeModel('deactivate')
              }
            })}
          />
        </Modal>
      </div>
      {((data && data.length > 0) || additionalMissingInformation.length > 0) && (
        <StaffMissingDocumentsContainer
          data={data ?? []}
          onMissingDocumentClick={onMissingDocumentClick}
          additionalMissingInformation={additionalMissingInformation}
        />
      )}
    </div>
  )
}

export default StaffActionsHeader
